/**
 * Helpers for rendering hCaptcha explicitly from Vue components.
 *
 * The hCaptcha script is loaded in public/index.html with
 * `?render=explicit&onload=hcaptchaOnLoad`. The onload callback sets
 * `window.__hcaptchaReady = true` and dispatches an `hcaptcha:ready` event.
 *
 * These helpers wait for that signal and then expose the standard
 * render / getResponse / reset / remove API, keyed by widget id so that
 * multiple widgets (sign-in, sign-up) don't interfere with each other.
 */

const READY_EVENT = "hcaptcha:ready";

/**
 * Resolves once the hCaptcha script is loaded and `window.hcaptcha` exists.
 * Rejects after `timeoutMs` (default 15s) if it never loads — typically
 * because an ad-blocker or network policy is blocking js.hcaptcha.com.
 */
export function whenHcaptchaReady(timeoutMs = 15000) {
  return new Promise((resolve, reject) => {
    if (window.__hcaptchaReady && window.hcaptcha) {
      resolve(window.hcaptcha);
      return;
    }

    let timer = null;

    const onReady = () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(READY_EVENT, onReady);
      resolve(window.hcaptcha);
    };

    window.addEventListener(READY_EVENT, onReady, { once: true });

    timer = setTimeout(() => {
      window.removeEventListener(READY_EVENT, onReady);
      reject(new Error("hCaptcha failed to load"));
    }, timeoutMs);
  });
}

/**
 * Explicitly render an hCaptcha widget into the given container element.
 * Returns the widget id (a string/number) which must be used with the
 * other helpers below.
 */
export async function renderHcaptcha(container, sitekey, options = {}) {
  if (!container) {
    throw new Error("renderHcaptcha: container element is required");
  }
  const hcaptcha = await whenHcaptchaReady();
  return hcaptcha.render(container, { sitekey, ...options });
}

/**
 * Read the response token for a specific widget. Returns an empty string
 * if the widget isn't ready or hasn't been solved.
 */
export function getHcaptchaResponse(widgetId) {
  if (widgetId === null || widgetId === undefined) return "";
  if (!window.hcaptcha) return "";
  try {
    return window.hcaptcha.getResponse(widgetId) || "";
  } catch (e) {
    return "";
  }
}

/** Reset a specific widget so the user can solve it again. */
export function resetHcaptcha(widgetId) {
  if (widgetId === null || widgetId === undefined) return;
  if (!window.hcaptcha) return;
  try {
    window.hcaptcha.reset(widgetId);
  } catch (e) {
    /* no-op */
  }
}

/** Remove a widget from the DOM. Safe to call during component unmount. */
export function removeHcaptcha(widgetId) {
  if (widgetId === null || widgetId === undefined) return;
  if (!window.hcaptcha) return;
  try {
    window.hcaptcha.remove(widgetId);
  } catch (e) {
    /* no-op */
  }
}
